import React from 'react';
import { Users, Stethoscope, Activity, Video, HeartPulse } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Services = () => {
  const services = [
    {
      id: 'staffing',
      icon: Users,
      title: 'Staffing Solutions',
      description:
        'Comprehensive healthcare staffing services tailored to your needs. We provide qualified healthcare professionals for short-term and long-term positions.',
      features: [
        'Temporary Staffing',
        'Permanent Placement',
        'Travel Nursing',
        'Per Diem Staffing',
      ],
      image: 'https://images.unsplash.com/photo-1638202993928-7267aad84c31?w=800&h=600&fit=crop',
    },
    {
      id: 'physicians',
      icon: Stethoscope,
      title: 'Physicians & Advanced Practice',
      description:
        'Connect with top physicians and advanced practice providers. Our network includes board-certified physicians across multiple specialties.',
      features: [
        'Board-Certified Physicians',
        'Nurse Practitioners',
        'Physician Assistants',
        'Specialized Care Providers',
      ],
      image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=800&h=600&fit=crop',
    },
    {
      id: 'locum',
      icon: Activity,
      title: 'Locum Tenens',
      description:
        'Flexible locum tenens staffing solutions for healthcare facilities. Fill temporary physician gaps with qualified locum tenens professionals.',
      features: [
        'Temporary Coverage',
        'Emergency Staffing',
        'Vacation Coverage',
        'Peak Season Support',
      ],
      image: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=800&h=600&fit=crop',
    },
    {
      id: 'telehealth',
      icon: Video,
      title: 'Telehealth',
      description:
        'Modern telehealth staffing for the digital healthcare era. We provide qualified professionals experienced in virtual care delivery.',
      features: [
        'Virtual Consultations',
        'Remote Monitoring',
        'Digital Health Services',
        'Telemedicine Support',
      ],
      image: 'https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?w=800&h=600&fit=crop',
    },
    {
      id: 'nurses',
      icon: HeartPulse,
      title: 'Nurses',
      description:
        'Skilled nursing professionals ready to provide exceptional care. Our nursing staff includes RNs, LPNs, and specialized nursing professionals.',
      features: [
        'Registered Nurses (RN)',
        'Licensed Practical Nurses (LPN)',
        'Certified Nursing Assistants',
        'Specialty Nursing',
      ],
      image: 'https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?w=800&h=600&fit=crop',
    },
  ];

  return (
    <div className="services-page">
      <PageMeta
        title="Services"
        description="Healthcare staffing solutions: staffing, physicians & advanced practice, locum tenens, telehealth, and nursing. Tailored to your facility."
        path="/services"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Our Services</h1>
            <p className="text-xl leading-relaxed">
              Comprehensive healthcare staffing and talent solutions for every need
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          {services.map((service, index) => (
            <div key={service.id} id={service.id} className="mb-20">
              <div className={`grid md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}>
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <service.icon className="w-8 h-8 text-cyan-600" />
                  </div>
                  <h2 className="text-4xl font-bold mb-4">{service.title}</h2>
                  <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="space-y-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-cyan-600 rounded-full"></div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
                    Learn More
                  </Button>
                </div>
                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-lg shadow-xl"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
            <p className="text-xl mb-8">
              Contact us today to discuss your healthcare staffing needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-white text-cyan-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
              </Button>
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-cyan-600 px-8 py-6 text-lg">
                Call Us Now
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;